import React, { useMemo } from 'react';
import StravaLogo from '@strava/ui/StravaLogo';
import { EXPERIMENT_COHORTS } from '@strava/constants/src/appConstants';
import CPRAComplianceCTA from '@strava/cpra-compliance-cta';
import clsx from '@strava/ui/clsx';
import AppStoreBadges from '@strava/shared-ui/src/AppStoreBadges';
import SocialLink from './components/SocialLink';
import { useFrontDoorsPageContainerContext } from '../../state/FrontDoorsPageContainerContext';

import socialTwitterImage from '../../media/social_tw.svg';
import socialYouTubeImage from '../../media/social_yt.svg';
import socialInstagramImage from '../../media/social_ig.svg';
import socialLinkedInImage from '../../media/social_li.svg';
import socialFacebookImage from '../../media/social_fb.svg';

import styles from './Footer.module.scss';

const Footer = () => {
  const { t, state } = useFrontDoorsPageContainerContext();
  const { cohort, dataSharingOptedOut, cpraI18nPrefix, menuConfig, userAgent } =
    state;
  const { showLoginOptionOnly } = menuConfig;

  const socialLinks = useMemo(
    () => [
      {
        id: 1,
        url: 'https://twitter.com/strava',
        image: socialTwitterImage,
        altText: t('footer.social.alt.twitter')
      },
      {
        id: 2,
        url: 'https://www.youtube.com/stravainc',
        image: socialYouTubeImage,
        altText: t('footer.social.alt.youtube')
      },
      {
        id: 3,
        url: 'https://www.instagram.com/strava/',
        image: socialInstagramImage,
        altText: t('footer.social.alt.instagram')
      },
      {
        id: 4,
        url: 'https://www.linkedin.com/company/strava-inc.',
        image: socialLinkedInImage,
        altText: t('footer.social.alt.linkedin')
      },
      {
        id: 5,
        url: 'https://www.facebook.com/Strava',
        image: socialFacebookImage,
        altText: t('footer.social.alt.facebook')
      }
    ],
    [t]
  );

  // TODO - cleanup after experiment https://strava.atlassian.net/browse/ACQN-301
  const signUpLinkCopy = () => {
    // join_us -> 'Join For Free'
    const key =
      cohort === EXPERIMENT_COHORTS.variantA
        ? 'header.join_now'
        : 'footer.links.join_us';
    return t(key);
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <section className={styles.privacySocial}>
          <StravaLogo height="23px" alt="Strava" />
          <div>
            <p>
              {t('footer.privacy.strava_protects_your_data')}
              <br />
              {t('footer.privacy.read_more_in_our')}{' '}
              <a href="/legal/privacy">{t('footer.privacy.privacy_policy')}</a>.
            </p>
            <div
              role="navigation"
              className={styles.social}
              aria-label={t('footer.social.aria_label')}
            >
              {socialLinks.map((socialLink) => (
                <SocialLink
                  url={socialLink.url}
                  image={socialLink.image}
                  altText={socialLink.altText}
                  key={socialLink.id}
                />
              ))}
            </div>
          </div>
        </section>
        <section
          className={styles.links}
          role="navigation"
          aria-label={t('footer.links.aria_label')}
        >
          <ul>
            <li>
              <ul>
                <li>
                  <a href="/features">{t('footer.links.features')}</a>
                </li>
                <li>
                  <a href="/gopremium">{t('footer.links.subscription')}</a>
                </li>
                <li>
                  <a href="/student?origin=global_footer">
                    {t('footer.links.student_discount')}
                  </a>
                </li>
                <li>
                  <a href="/about">{t('footer.links.about')}</a>
                </li>
                <li>
                  <a href="/careers">{t('footer.links.careers')}</a>
                </li>
                <li>
                  <a href="https://press.strava.com">
                    {t('footer.links.press')}
                  </a>
                </li>
              </ul>
            </li>
            <li>
              <ul>
                <li>
                  <a href="/routes/hiking/usa">{t('footer.links.routes')}</a>
                </li>
                <li>
                  <a href="/events/paris-marathon">
                    {t('footer.links.paris_2023_marathon')}
                  </a>
                </li>
                <li>
                  <a href="/events/boston-marathon">
                    {t('footer.links.boston_2023_marathon')}
                  </a>
                </li>
                <li>
                  <a href="/events/london-marathon">
                    {t('footer.links.london_2023_marathon')}
                  </a>
                </li>
              </ul>
            </li>
            <li>
              <ul>
                <li>
                  <a href="/whats-new">{t('footer.links.whats_new')}</a>
                </li>
                <li>
                  <a href="https://blog.strava.com/">
                    {t('footer.links.blog')}
                  </a>
                </li>
                <li>
                  <a href="https://strava.zendesk.com/home">
                    {t('footer.links.support')}
                  </a>
                </li>
                <li>
                  <a href="https://business.strava.com/?utm_source=footer&utm_medium=referral">
                    {t('footer.links.business')}
                  </a>
                </li>
                <li>
                  <a href="/legal/terms">{t('footer.links.terms')}</a>
                </li>
              </ul>
            </li>
            <li>
              <ul>
                <li>
                  <a href="/legal/privacy">{t('footer.links.privacy')}</a>
                </li>
                <li>
                  <CPRAComplianceCTA
                    // making an assumption here that front doors page container will never be shown on a logged-in page;
                    // if that changes, we'd need to pass in `isLoggedIn`
                    i18nPrefix={cpraI18nPrefix}
                    className={styles.cpraComplianceCta}
                    dataSharingOptedOut={dataSharingOptedOut}
                  />
                </li>
                <li>
                  <a
                    href="/login"
                    className={clsx(showLoginOptionOnly && styles.highlight)}
                  >
                    {t('footer.links.login')}
                  </a>
                </li>
                {!showLoginOptionOnly && (
                  <li>
                    <a
                      href="/register/free?cta=sign-up&element=global-footer&source=website_show"
                      className={styles.highlight}
                      data-cy="footer_join_us"
                    >
                      {signUpLinkCopy()}
                    </a>
                  </li>
                )}
              </ul>
            </li>
            <li>
              <AppStoreBadges userAgent={userAgent} />
            </li>
          </ul>
        </section>
      </div>
    </footer>
  );
};

export default Footer;
